import React, { useState, useEffect, useRef, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { Checkbox } from 'primereact/checkbox'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Link } from 'react-router-dom'

import server from '../../server'
import MainContentLayout from '../Layout/MainContentLayout'
import { InputFieldTemplate } from '../shared/FilterTemplates'
import { formatDate } from '../shared/utils'
import ModifyCatalogueItem from './ModifyCatalogueItem'

import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const narcoticsOptions = [
  { label: 'All narcotics rules', value: 'all' },
  { label: 'Narcotics only', value: 'only' },
  { label: 'Exclude narcotics', value: 'exclude' },
]

const discontinuedOptions = [
  { label: 'All', value: 'all' },
  { label: 'Active (not discontinued)', value: 'false' },
  { label: 'Discontinued', value: 'true' },
]

const backlogOptions = [
  { label: 'All', value: 'all' },
  { label: 'Not in backlog', value: 'false' },
  { label: 'In backlog', value: 'true' },
]

const PROVINCE_ORDER = [
  'NL',
  'PE',
  'NS',
  'NB',
  'QC',
  'ON',
  'MB',
  'SK',
  'AB',
  'BC',
  'YT',
  'NT',
  'NU',
]

/** Human-readable province prices; returns { short, title } for table cell + tooltip */
const formatProvincePrices = (v) => {
  if (v == null || v === '') {
    return { short: '—', title: '' }
  }
  let obj = v
  if (typeof v === 'string') {
    try {
      obj = JSON.parse(v)
    } catch {
      return { short: 'Invalid JSON', title: String(v) }
    }
  }
  if (typeof obj !== 'object' || obj == null) {
    return { short: '—', title: '' }
  }
  const orderedKeys = [
    ...PROVINCE_ORDER.filter((k) => Object.prototype.hasOwnProperty.call(obj, k)),
    ...Object.keys(obj).filter((k) => !PROVINCE_ORDER.includes(k)),
  ]
  const fullLines = orderedKeys.map((code) => {
    const raw = obj[code]
    const display =
      raw == null || raw === '' ? '—' : Number.isFinite(Number(raw)) ? Number(raw) : raw
    return `${code}: ${display}`
  })
  const title = fullLines.join('\n')
  const priced = orderedKeys.filter((code) => {
    const raw = obj[code]
    return raw != null && raw !== ''
  })
  if (priced.length === 0) {
    return { short: 'All provinces empty', title }
  }
  const shortParts = priced.map((code) => {
    const raw = obj[code]
    const n = Number(raw)
    return `${code}: ${Number.isFinite(n) ? n : raw}`
  })
  let short = shortParts.join(' · ')
  if (short.length > 96) short = `${short.slice(0, 96)}…`
  return { short, title }
}

export default function CatalogueAdminTable() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [rows, setRows] = useState(null)
  const [totalRecords, setTotalRecords] = useState(0)
  const [tableLoading, setTableLoading] = useState(false)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [lazyState, setLazyState] = useState(() =>
    getInitialPaginationState(user, {
      sortField: 'id',
      sortOrder: -1,
      filters: {
        drugIdentificationNo: { value: '' },
        retailUpcCode: { value: '' },
        manufacturer: { value: '' },
        itemDescription: { value: '' },
      },
    })
  )
  const [searchQ, setSearchQ] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [narcoticsFilter, setNarcoticsFilter] = useState('all')
  const [discontinuedFilter, setDiscontinuedFilter] = useState('all')
  const [backlogFilter, setBacklogFilter] = useState('all')
  const [showFiltersPanel, setShowFiltersPanel] = useState(false)

  const dt = useRef()
  const AddEditRef = useRef()
  const DeleteRef = useRef()
  const refs = { AddEditRef, DeleteRef }

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const initLazyState = () => {
    setLazyState({
      rows: defaultRows,
      page: 0,
      first: 0,
      sortField: 'id',
      sortOrder: -1,
      filters: {
        drugIdentificationNo: { value: '' },
        retailUpcCode: { value: '' },
        manufacturer: { value: '' },
        itemDescription: { value: '' },
      },
    })
    setSearchQ('')
    setIncludeDeleted(false)
    setNarcoticsFilter('all')
    setDiscontinuedFilter('all')
    setBacklogFilter('all')
  }

  const getData = () => {
    const params = new URLSearchParams()
    params.set('page', lazyState?.page != null ? lazyState.page + 1 : 1)
    params.set('limit', lazyState?.rows ?? defaultRows)
    const din = lazyState?.filters?.drugIdentificationNo?.value
    const upc = lazyState?.filters?.retailUpcCode?.value
    const manufacturer = lazyState?.filters?.manufacturer?.value
    const itemDescription = lazyState?.filters?.itemDescription?.value
    if (din) params.set('drugIdentificationNo', din)
    if (upc) params.set('retailUpcCode', upc)
    if (manufacturer) params.set('manufacturer', manufacturer)
    if (itemDescription) params.set('itemDescription', itemDescription)
    if (searchQ.trim()) params.set('q', searchQ.trim())
    if (includeDeleted) params.set('includeDeleted', 'true')
    if (narcoticsFilter && narcoticsFilter !== 'all') {
      params.set('narcotics', narcoticsFilter)
    }
    if (discontinuedFilter && discontinuedFilter !== 'all') {
      params.set('discontinued', discontinuedFilter)
    }
    if (backlogFilter && backlogFilter !== 'all') {
      params.set('backlog', backlogFilter)
    }
    if (lazyState?.sortField) params.set('sortByField', lazyState.sortField)
    if (lazyState?.sortOrder != null) {
      params.set('sortByOrder', String(lazyState.sortOrder))
    }

    setTableLoading(true)
    server
      .get(`/admin/catalogue-items?${params.toString()}`)
      .then((response) => {
        setRows(response.data.data)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
      })
      .catch(() => {
        setTableLoading(false)
      })
  }

  useEffect(() => {
    checkClearBtn()
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyState, searchQ, includeDeleted, narcoticsFilter, discontinuedFilter, backlogFilter])

  const checkClearBtn = () => {
    if (lazyState?.sortField && lazyState.sortField !== 'id') {
      setClearBtnDisabled(false)
      return
    }
    if (lazyState?.sortOrder != null && lazyState.sortOrder !== -1) {
      setClearBtnDisabled(false)
      return
    }
    if (searchQ.trim()) {
      setClearBtnDisabled(false)
      return
    }
    if (includeDeleted) {
      setClearBtnDisabled(false)
      return
    }
    if (narcoticsFilter !== 'all') {
      setClearBtnDisabled(false)
      return
    }
    if (discontinuedFilter !== 'all') {
      setClearBtnDisabled(false)
      return
    }
    if (backlogFilter !== 'all') {
      setClearBtnDisabled(false)
      return
    }
    for (const key of Object.keys(lazyState?.filters || {})) {
      if (lazyState.filters[key]?.value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const onPage = (event) =>
    setLazyState((prev) => applyPageEvent(prev, event))
  const onSort = (event) => setLazyState(event)
  const onFilter = (event) => setLazyState(event)

  const dinFilter = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='DIN'
    />
  )

  const upcFilter = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='UPC'
    />
  )

  const manufacturerFilter = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Manufacturer'
    />
  )

  const descriptionFilter = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Description contains'
    />
  )

  const moleculeBody = (row) => {
    const din = row.drugIdentificationNo
    const name = row.molecule?.name
    const dinStr =
      din != null && din !== '' ? String(din).padStart(8, '0') : null
    if (!name && !dinStr) {
      return <span className='text-muted'>—</span>
    }
    return (
      <div className='d-flex flex-column gap-1'>
        {name ? <span>{name}</span> : null}
        {dinStr ? (
          <Link to={`/admin/reports/molecules/${din}`} state={row.molecule}>
            Open molecule ({dinStr})
          </Link>
        ) : null}
      </div>
    )
  }

  const boolTag = (truthy, yesLabel, noLabel) => (
    <Tag
      severity={truthy ? 'danger' : 'success'}
      value={truthy ? yesLabel : noLabel}
    />
  )

  const activeToolbarFilterCount =
    (includeDeleted ? 1 : 0) +
    (narcoticsFilter !== 'all' ? 1 : 0) +
    (discontinuedFilter !== 'all' ? 1 : 0) +
    (backlogFilter !== 'all' ? 1 : 0)

  const actionBody = (rowData) => (
    <div className='d-flex justify-content-center gap-2'>
      <Button
        icon='pi pi-pencil'
        rounded
        outlined
        onClick={() => AddEditRef.current.editRow(rowData)}
      />
      {!rowData.isSoftDeleted ? (
        <Button
          icon='pi pi-trash'
          rounded
          outlined
          severity='danger'
          onClick={() => DeleteRef.current.confirmDelete(rowData)}
        />
      ) : null}
    </div>
  )

  const leftToolbar = () => (
    <div className='d-flex flex-wrap align-items-center gap-2'>
      <Button
        type='button'
        icon='pi pi-filter'
        label='Filters'
        outlined={!showFiltersPanel}
        severity={showFiltersPanel ? 'info' : undefined}
        onClick={() => setShowFiltersPanel((open) => !open)}
        badge={activeToolbarFilterCount > 0 ? String(activeToolbarFilterCount) : null}
        aria-expanded={showFiltersPanel}
      />
      <Button
        type='button'
        icon='pi pi-filter-slash'
        label='Clear'
        outlined
        onClick={initLazyState}
        disabled={clearBtnDisabled}
      />
      <InputText
        value={searchQ}
        onChange={(e) => setSearchQ(e.target.value)}
        placeholder='Search UPC, DIN, description, manufacturer…'
        style={{ minWidth: '18rem' }}
      />
    </div>
  )

  const filtersPanel = () =>
    showFiltersPanel ? (
      <div className='catalogue-admin-filters-panel border-top bg-light px-3 py-3 mb-2'>
        <div className='row g-3 align-items-end'>
          <div className='col-12 col-sm-6 col-lg-3'>
            <label htmlFor='incDel' className='form-label small text-muted mb-1'>
              Soft-deleted
            </label>
            <div className='d-flex align-items-center gap-2 pt-1'>
              <Checkbox
                inputId='incDel'
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(!!e.checked)}
              />
              <label htmlFor='incDel' className='mb-0'>
                Show soft-deleted
              </label>
            </div>
          </div>
          <div className='col-12 col-sm-6 col-lg-3'>
            <label htmlFor='catalogue-narcotics-filter' className='form-label small text-muted mb-1'>
              Narcotics
            </label>
            <Dropdown
              inputId='catalogue-narcotics-filter'
              value={narcoticsFilter}
              options={narcoticsOptions}
              onChange={(e) => setNarcoticsFilter(e.value)}
              optionLabel='label'
              optionValue='value'
              className='w-100'
            />
          </div>
          <div className='col-12 col-sm-6 col-lg-3'>
            <label htmlFor='catalogue-discontinued-filter' className='form-label small text-muted mb-1'>
              Discontinued
            </label>
            <Dropdown
              inputId='catalogue-discontinued-filter'
              value={discontinuedFilter}
              options={discontinuedOptions}
              onChange={(e) => setDiscontinuedFilter(e.value)}
              optionLabel='label'
              optionValue='value'
              className='w-100'
            />
          </div>
          <div className='col-12 col-sm-6 col-lg-3'>
            <label htmlFor='catalogue-backlog-filter' className='form-label small text-muted mb-1'>
              Backlog
            </label>
            <Dropdown
              inputId='catalogue-backlog-filter'
              value={backlogFilter}
              options={backlogOptions}
              onChange={(e) => setBacklogFilter(e.value)}
              optionLabel='label'
              optionValue='value'
              className='w-100'
            />
          </div>
        </div>
      </div>
    ) : null

  const rightToolbar = () => (
    <Button
      label='New catalogue row'
      icon='pi pi-plus'
      severity='success'
      onClick={() => AddEditRef.current.openNew()}
    />
  )

  return (
    <MainContentLayout title='Catalogue (admin)'>
      <div className='card'>
        <Toolbar className='mb-0 catalogue-admin-toolbar' left={leftToolbar} right={rightToolbar} />
        {filtersPanel()}
        <div className='table-responsive' style={{ maxWidth: '100%' }}>
          <DataTable
            ref={dt}
            value={rows}
            paginator
            lazy
            showGridlines
            removableSort
            rowsPerPageOptions={rowsPerPageOptions}
            totalRecords={totalRecords}
            loading={tableLoading}
            dataKey='id'
            emptyMessage='No catalogue rows found.'
            filters={lazyState?.filters}
            sortField={lazyState?.sortField}
            sortOrder={lazyState?.sortOrder}
            rows={lazyState?.rows}
            first={lazyState?.first}
            onPage={onPage}
            onSort={onSort}
            onFilter={onFilter}
            scrollable
            scrollHeight='62vh'
          >
            <Column field='id' header='ID' sortable style={{ minWidth: '5rem' }} />
            <Column
              field='retailUpcCode'
              header='UPC'
              sortable
              style={{ minWidth: '10rem' }}
              filter
              filterElement={upcFilter}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
            />
            <Column
              field='drugIdentificationNo'
              header='DIN'
              sortable
              style={{ minWidth: '7rem' }}
              filter
              filterElement={dinFilter}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={(r) =>
                r.drugIdentificationNo != null && r.drugIdentificationNo !== ''
                  ? String(r.drugIdentificationNo).padStart(8, '0')
                  : '—'
              }
            />
            <Column
              header='Molecule'
              style={{ minWidth: '14rem' }}
              body={moleculeBody}
            />
            <Column
              field='manufacturer.name'
              header='Manufacturer'
              style={{ minWidth: '11rem' }}
              filter
              filterField='manufacturer'
              filterElement={manufacturerFilter}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={(r) => r.manufacturer?.name || '—'}
            />
            <Column
              field='itemDescription'
              header='Description'
              sortable
              style={{ minWidth: '18rem' }}
              filter
              filterElement={descriptionFilter}
              showFilterMatchModes={false}
              showFilterOperator={false}
              showAddButton={false}
              body={(r) => (
                <span title={r.itemDescription || ''}>
                  {r.itemDescription
                    ? r.itemDescription.length > 80
                      ? `${r.itemDescription.slice(0, 80)}…`
                      : r.itemDescription
                    : '—'}
                </span>
              )}
            />
            <Column
              field='strength'
              header='Strength'
              sortable
              style={{ minWidth: '10rem' }}
              body={(r) => r.strength || '—'}
            />
            <Column
              field='discontinued'
              header='Discontinued'
              sortable
              style={{ minWidth: '8rem' }}
              body={(r) => boolTag(r.discontinued, 'Yes', 'No')}
            />
            <Column
              field='backlog'
              header='Backlog'
              sortable
              style={{ minWidth: '7rem' }}
              body={(r) => boolTag(!!r.backlog, 'Yes', 'No')}
            />
            <Column
              field='isNarcotic'
              header='Narcotic'
              style={{ minWidth: '7rem' }}
              body={(r) => boolTag(r.isNarcotic, 'Yes', 'No')}
            />
            <Column
              field='itemGroupMinorDescription'
              header='Minor group'
              style={{ minWidth: '9rem' }}
              body={(r) => r.itemGroupMinorDescription || '—'}
            />
            <Column
              field='itemGroupMajorDescription'
              header='Major group'
              sortable
              style={{ minWidth: '11rem' }}
              body={(r) => r.itemGroupMajorDescription || '—'}
            />
            <Column
              field='narcoticCode'
              header='Narc. code'
              style={{ minWidth: '6rem' }}
              body={(r) =>
                r.narcoticCode != null && r.narcoticCode !== ''
                  ? r.narcoticCode
                  : '—'
              }
            />
            <Column
              field='sellingPrice'
              header='Price'
              sortable
              style={{ minWidth: '7rem' }}
              body={(r) =>
                r.sellingPrice != null ? Number(r.sellingPrice).toFixed(4) : '—'
              }
            />
            <Column
              field='sellingPriceUnit'
              header='Price unit'
              style={{ minWidth: '6rem' }}
              body={(r) => r.sellingPriceUnit || '—'}
            />
            <Column
              header='Prov. prices'
              style={{ minWidth: '12rem' }}
              body={(r) => {
                const { short, title } = formatProvincePrices(r.sellingPricePR)
                return (
                  <span
                    title={title || undefined}
                    style={{ whiteSpace: title ? 'normal' : undefined }}
                  >
                    {short}
                  </span>
                )
              }}
            />
            <Column
              field='packSize'
              header='Pack'
              sortable
              style={{ minWidth: '5rem' }}
            />
            <Column
              field='minimumOrderQuantity'
              header='Min Qty'
              style={{ minWidth: '6rem' }}
            />
            <Column
              field='multipleOrderQuantity'
              header='Mult. Qty'
              style={{ minWidth: '6rem' }}
            />
            <Column
              field='maxOrderQuantityPerOrder'
              header='Max / order'
              sortable
              style={{ minWidth: '7rem' }}
              body={(r) =>
                r.maxOrderQuantityPerOrder != null &&
                r.maxOrderQuantityPerOrder !== ''
                  ? r.maxOrderQuantityPerOrder
                  : '—'
              }
            />
            <Column
              field='itemCode'
              header='Item code'
              sortable
              style={{ minWidth: '7rem' }}
            />
            <Column
              field='itemTypeCode'
              header='Type code'
              style={{ minWidth: '7rem' }}
            />
            <Column
              field='itemTypeShortDescription'
              header='Type (short)'
              style={{ minWidth: '9rem' }}
              body={(r) => r.itemTypeShortDescription || '—'}
            />
            <Column
              field='itemTypeDescription'
              header='Type'
              style={{ minWidth: '10rem' }}
              body={(r) => r.itemTypeDescription || '—'}
            />
            <Column
              field='itemGroupMajorCode'
              header='Maj code'
              style={{ minWidth: '6rem' }}
            />
            <Column
              field='itemGroupMinorCode'
              header='Min code'
              style={{ minWidth: '6rem' }}
            />
            <Column
              field='defaultSupplier'
              header='Default supplier'
              style={{ minWidth: '9rem' }}
              body={(r) => r.defaultSupplier || '—'}
            />
            <Column
              field='priceFileType'
              header='Price file type'
              style={{ minWidth: '8rem' }}
              body={(r) => r.priceFileType || '—'}
            />
            <Column
              field='provincialPriceListCode'
              header='Prov. price list'
              style={{ minWidth: '8rem' }}
              body={(r) => r.provincialPriceListCode || '—'}
            />
            <Column
              field='provincialCode'
              header='Prov. code'
              style={{ minWidth: '7rem' }}
              body={(r) => r.provincialCode || '—'}
            />
            <Column
              field='supplierId'
              header='Supplier ID'
              style={{ minWidth: '7rem' }}
            />
            <Column
              field='sellingConversionFactor'
              header='Sell conv.'
              style={{ minWidth: '7rem' }}
            />
            <Column
              field='isSoftDeleted'
              header='Deleted'
              style={{ minWidth: '7rem' }}
              body={(r) => boolTag(r.isSoftDeleted, 'Yes', 'No')}
            />
            <Column
              field='deletedAt'
              header='Deleted at'
              style={{ minWidth: '9rem' }}
              body={(r) =>
                r.deletedAt ? formatDate(new Date(r.deletedAt)) : '—'
              }
            />
            <Column
              field='createdAt'
              header='Created'
              sortable
              style={{ minWidth: '9rem' }}
              body={(r) =>
                r.createdAt ? formatDate(new Date(r.createdAt)) : '—'
              }
            />
            <Column
              field='updatedAt'
              header='Updated'
              sortable
              style={{ minWidth: '9rem' }}
              body={(r) =>
                r.updatedAt ? formatDate(new Date(r.updatedAt)) : '—'
              }
            />
            <Column
              body={actionBody}
              exportable={false}
              style={{ minWidth: '8rem' }}
            />
          </DataTable>
        </div>
        <ModifyCatalogueItem ref={refs} getData={getData} />
      </div>
    </MainContentLayout>
  )
}
